import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { SettingsPageHeader } from '@/components/settings/SettingsPageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Loader2, DollarSign, RefreshCw, Check, ArrowRightLeft } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';

export default function CurrencySettingsPage() {
  const [currencies, setCurrencies] = useState([]);
  const { t } = useLanguage();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fetchingRates, setFetchingRates] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [cRes, cfgRes] = await Promise.all([
          api.get('/settings/currencies'),
          api.get('/settings/currency'),
        ]);
        setCurrencies(cRes.data);
        setConfig(cfgRes.data);
      } catch { toast.error('Failed to load'); }
      finally { setLoading(false); }
    })();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.put('/settings/currency', config);
      setConfig(res.data);
      toast.success('Currency settings saved');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const handleRateChange = (code, rate) => {
    setConfig(prev => ({
      ...prev,
      exchange_rates: { ...prev.exchange_rates, [code]: parseFloat(rate) || 0 },
    }));
  };

  const toggleSecondary = (code) => {
    setConfig(prev => {
      const list = prev.secondary_currencies || [];
      return {
        ...prev,
        secondary_currencies: list.includes(code) ? list.filter(c => c !== code) : [...list, code],
      };
    });
  };

  const handleFetchLiveRates = async () => {
    setFetchingRates(true);
    try {
      const res = await api.post('/settings/currency/fetch-rates');
      if (res.data.rates) {
        setConfig(prev => ({
          ...prev,
          exchange_rates: { ...prev.exchange_rates, ...res.data.rates },
          rates_updated_at: res.data.updated_at,
        }));
        toast.success(`Live rates fetched from ${res.data.source}`);
      } else {
        toast.info(res.data.message || 'No rates to fetch');
      }
    } catch { toast.error('Failed to fetch live rates'); }
    finally { setFetchingRates(false); }
  };

  if (loading) return <DashboardLayout><div className="flex items-center justify-center h-64"><Loader2 className="animate-spin" /></div></DashboardLayout>;
  if (!config) return <DashboardLayout><div className="text-center py-12 text-muted-foreground">Failed to load settings</div></DashboardLayout>;

  const primaryCurrency = currencies.find(c => c.code === config.primary_currency) || currencies[0];

  return (
    <DashboardLayout>
      <div className="space-y-5 max-w-3xl" data-testid="currency-settings">
        <SettingsPageHeader
          icon={DollarSign}
          title={t('currency_settings_title') || 'Currency Settings'}
          subtitle="Configure your primary and secondary currencies and exchange rates."
          accent="emerald"
          titleTestId="page-title"
        />

        <Card>
          <CardHeader><CardTitle className="text-sm flex items-center gap-2"><DollarSign size={16} />Primary Currency</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Primary Currency</Label>
              <Select value={config.primary_currency} onValueChange={v => setConfig({ ...config, primary_currency: v })}>
                <SelectTrigger data-testid="primary-currency-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {currencies.map(c => (
                    <SelectItem key={c.code} value={c.code}>{c.symbol} {c.name} ({c.code})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Display Format</Label>
              <Select value={config.display_format} onValueChange={v => setConfig({ ...config, display_format: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="symbol_before">{primaryCurrency?.symbol} 1,000.00</SelectItem>
                  <SelectItem value="symbol_after">1,000.00 {primaryCurrency?.symbol}</SelectItem>
                  <SelectItem value="code_before">{primaryCurrency?.code} 1,000.00</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="p-3 bg-muted/50 rounded-lg border">
              <Label className="text-xs text-muted-foreground">Preview</Label>
              <div className="text-lg font-bold font-outfit mt-1">
                {config.display_format === 'symbol_after' ? `1,250.00 ${primaryCurrency?.symbol}` :
                 config.display_format === 'code_before' ? `${primaryCurrency?.code} 1,250.00` :
                 `${primaryCurrency?.symbol} 1,250.00`}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm flex items-center gap-2"><ArrowRightLeft size={16} />Secondary Currencies & Exchange Rates</CardTitle>
              <Button size="sm" variant="outline" className="rounded-full text-xs" onClick={handleFetchLiveRates} disabled={fetchingRates || !(config.secondary_currencies || []).length} data-testid="fetch-live-rates-btn">
                <RefreshCw size={12} className={`mr-1 ${fetchingRates ? 'animate-spin' : ''}`} />
                {fetchingRates ? 'Fetching...' : 'Fetch Live Rates'}
              </Button>
            </div>
            {config.rates_updated_at && (
              <p className="text-[10px] text-muted-foreground mt-1">Last updated: {new Date(config.rates_updated_at).toLocaleString()}</p>
            )}
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground mb-3">Enable secondary currencies and set exchange rates relative to {config.primary_currency}</p>
            <div className="space-y-2">
              {currencies.filter(c => c.code !== config.primary_currency).map(c => {
                const enabled = (config.secondary_currencies || []).includes(c.code);
                return (
                  <div key={c.code} className={`flex items-center justify-between p-3 rounded-lg border ${enabled ? 'bg-info/10 border-info/30' : 'bg-muted/50'}`} data-testid={`currency-${c.code}`}>
                    <div className="flex items-center gap-3">
                      <Switch checked={enabled} onCheckedChange={() => toggleSecondary(c.code)} data-testid={`toggle-${c.code}`} />
                      <div>
                        <span className="text-sm font-medium">{c.symbol} {c.name}</span>
                        <span className="text-xs text-muted-foreground ml-2">{c.code}</span>
                      </div>
                    </div>
                    {enabled && (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">1 {config.primary_currency} =</span>
                        <Input type="number" step="0.001" value={config.exchange_rates?.[c.code] || ''} onChange={e => handleRateChange(c.code, e.target.value)} className="w-24 h-7 text-xs" placeholder="Rate" data-testid={`rate-${c.code}`} />
                        <span className="text-xs">{c.code}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <Button onClick={handleSave} disabled={saving} className="w-full rounded-xl" data-testid="save-currency-btn">
          {saving ? <Loader2 size={14} className="animate-spin mr-1" /> : <Check size={14} className="mr-1" />}Save Currency Settings
        </Button>
      </div>
    </DashboardLayout>
  );
}
